import * as Hogan from 'hogan.js';
import { PayLinesGenerator} from '@gongxigames/framework/slotV2/modules/infopage';
import {ReelConstants} from '@gongxigames/framework/slot/utils';
import {InfoModule} from '@gongxigames/framework/slotV2/modules/info';
import {
    CommandLineInterface,
    consoleLogger,
    CoreDI,
    DeviceType, HTMLMovieClip, inject,
} from '@gongxigames/framework/core';
import {Image as ImageComponent} from '@gongxigames/framework/core/fragments/BuilderX';
import {ImagesMap} from '@gongxigames/framework/slot/services/TemplateUtils';
import {GameOnlyEventMap, slotStore, strictUiEmitter} from '@gongxigames/framework/slotV2/globals';

type AnimationsKeyFrames = [string, string[]][];
type ViewResources = [ImagesMap, AnimationsKeyFrames, string];
enum InitializationState {
    notInitialized,
    initialized,
    initializing,
}

export class InfoPage<T extends GameOnlyEventMap> extends InfoModule<T>{
    public static readonly NAME: symbol = Symbol('InfoPage');

    protected _infoPageContainer!: HTMLElement;
    protected _infoPage!: HTMLElement;

    @inject(CoreDI.CLI)
    private _cli!: CommandLineInterface;

    protected _isOpen = false;
    protected _initialized: InitializationState = InitializationState.notInitialized;

    public init(): void {
        super.init();

        this.createPage();

        /* develblock:start */
        this._cli.addCommand('infoPage', {
            show: () => {
                const menuBar = this._cli.getCommand('menuBar');

                // for mobile
                if (menuBar) {
                    if (typeof menuBar !== 'function') {
                        menuBar.showInfoPage();
                    }
                } else {
                    this.onPageClicked();
                }
            },
            close: () => {
                this.close();
                const menuBar = this._cli.getCommand('menuBar');

                if (menuBar && typeof menuBar !== 'function') {
                    menuBar.close();
                }
            }
        });
        /* develblock:end */
    }

    public destroy(): void {
        super.destroy();

        if (this._infoPageContainer && this._infoPageContainer.parentNode) {
            this._infoPageContainer.remove();
        }
    }

    protected onSetupEvents(): void {
        this.emitter.on('Game.command.hideInfo', this.close.bind(this));
        strictUiEmitter.on('UI.menu.infoPageClicked', this.onPageClicked.bind(this));
    }

    protected createPage(): void {
        this._infoPageContainer = document.createElement('div');
        this._infoPageContainer.id = 'infoPageContainer';
        document.body.appendChild(this._infoPageContainer);

        this._infoPage = document.createElement('div');
        this._infoPage.id = 'infoPage';
        this._infoPageContainer.appendChild(this._infoPage);
    }

    protected async setupView(): Promise<void> {
        const [images, kayFrames, templateString] = await this.load();

        this._infoPageContainer.appendChild(this._infoPage);

        this.insertHtml(templateString, images);
        this.insertAnimations(kayFrames);
    }

    protected async load(): Promise<ViewResources> {
        const templateString = await this._resourceManager.loadHTMLTemplate('infoPage');
        const [images, kayFrames] = await this.processImages(templateString);

        return [images, kayFrames, templateString];
    }

    protected async onPageClicked(): Promise<void> {
        if (this._isOpen) {
            return;
        }

        slotStore.soundPlayer.value?.play('opengamerules');

        this._isOpen = true;

        strictUiEmitter.emit('Game.command.round.uiDisabled', InfoPage.NAME);

        if (this._initialized === InitializationState.initializing) {
            return;
        } else if (this._initialized === InitializationState.notInitialized) {
            this._initialized = InitializationState.initializing;

            await this.setupView();

            this._initialized = InitializationState.initialized;
        }

        this.openPage();
    }

    protected openPage(): void {
        strictUiEmitter.emit('UI.infoPage.opened');
        this.updateMultiplierValues();
        this.updatePayValues();

        strictUiEmitter.emit('Game.command.showInfo', {
            name: InfoPage.NAME,
            content: this._infoPageContainer,
            title: this._languageService.get('paytable')
        });
    }

    protected close(): void {
        if (this._isOpen) {
            this._isOpen = false;
            this._infoPageContainer && strictUiEmitter.emit('UI.infoPage.closed');
        }
    }

    protected processImages(templateString: string): Promise<[ImagesMap, AnimationsKeyFrames]> {
        return new Promise(res => {
            const lang = this._settings.get('lang');
            const images = this._templateUtils.createImageMapAsync(templateString, lang);
            const keysFrames = this.processAnimationKeyframes(templateString);

            if (this.store.logic_isLinesGame.value) {
                images.lines = this.generateLines();
            }

            res([images, keysFrames]);
        });
    }

    protected processAnimationKeyframes(templateString: string): AnimationsKeyFrames {
        const keysFrames: [string, string[]][] = [];
        const animationKeys = this._templateUtils.getAnimationKeys(templateString);

        for (const animation of animationKeys) {
            const config = this._objectBuilder.getDisplayObjectConfigByName('paytableAssets', animation)!;

            if (!config) {
                consoleLogger.error(`There's no "${animation}" sprite in "paytableAssets" sprite`);

                continue;
            }

            const framesPromises = (config.props.children as any as ImageComponent[]).map((child) => {
                return this._displayObjectUtils.textureToBase64(this._loader.getTexture(child.props.src));
            }) as string[];

            keysFrames.push([animation, framesPromises]);
        }

        return keysFrames;
    }

    protected insertHtml(templateString: string, images: Record<string, string>): void {
        const template = Hogan.compile(templateString);
        const showOddsUpfront = this._settings.get('showOddsUpfront');
        const isWaysGame = this.store.logic_isWaysGame.value;
        const texts = {...this._languageService.texts};
        const valuesForTexts = {
            betPredictionFormulaText: {wager: this.store.logic_wager.value},
            betPredictionFormulaTextWays: {wager: this.store.logic_wager.value}
        };

        for (const [textID, data] of Object.entries(valuesForTexts)) {
            if (this._languageService.get(textID) !== 'undefined' && data !== undefined) {
                texts[textID] = this._languageService.get(textID, data);
            }
        }

        const html = template.render({
            images,
            texts,
            showOddsUpfront,
            isWaysGame
        });

        this._infoPage.insertAdjacentHTML('beforeend', html);
    }

    protected insertAnimations(keysFrames: [string, string[]][]): void {
        keysFrames.forEach(([animation, frames]) => {
            const element = (document.getElementById(`animations.${animation}`) as HTMLImageElement);
            const movieClip = new HTMLMovieClip(element, frames, Number(element.dataset.fps));

            this.store.updateManager.value!.add(movieClip);
            movieClip.start();
        });
    }

    protected generateLines(): string {
        const isMobile = this._settings.get('platform') === DeviceType.MOBILE;
        const width = isMobile ? 50 : 40;
        const height = isMobile ? 50 : 40;
        const elementsOnRow = isMobile ? 4 : 5;

        return new PayLinesGenerator({
            width,
            height,
            distToCenterNumber: 40,
            lineWidth: 6,
            font: {
                size: 65,
                family: 'PF DinDisplay Pro'
            },
            scale: this._screenManager.defaultViewportResolution,
            elementsOnRow,
            xSpaceBetweenElements: 200,
            ySpaceBetweenElements: 80,
            columns: ReelConstants.REELS_AMOUNT,
            rows: ReelConstants.ROW_AMOUNT,
            colors: {
                symbolCell: '#ffc000',
                emptyCell: '#363636'
            },
            lines: this.parseLines()
        })
            .generatePayLines()
            .toDataURL();
    }

    protected parseLines(): number[][] {
        const payLines = this.store.logic_lines.value!;
        const lines = [];

        for (const payLine of payLines) {
            const line = [];

            for (let reel = 0; reel < ReelConstants.REELS_AMOUNT; reel++) {
                line.push(payLine.getRow(reel));
            }

            lines.push(line);
        }

        return lines;
    }

    protected updateMultiplierValues(): void {
        const multiplierSignFirst = this._settings.get('multiplierSignFirst');

        if (!multiplierSignFirst) {
            return;
        }

        const pays = document
            .querySelectorAll('.yellow-text li, .gray-text li, span.yellow-text, span.gray-text') as any;

        for (const pay of pays) {
            const valueArr = pay.innerHTML.split('');

            if (valueArr[valueArr.length - 1] === 'x') {
                valueArr.length--;
                pay.innerHTML = 'x' + valueArr.join('');
            }
        }
    }

    protected updatePayValues(): void {
        const pays = document.querySelectorAll('.pays') as any;
        const showOddsUpfront = this._settings.get('showOddsUpfront');

        for (const pay of pays) {
            const [symbolId, symbolsAmount] = pay.classList[1].split('*');
            const betFactor = this.store.logic_betFactor.value(symbolId);
            let amount = this.store.logic_slotSymbol.value(symbolId).paytable[symbolsAmount];

            if (!showOddsUpfront) {
                amount *= betFactor;
            } else if (betFactor === this.store.logic_totalBet.value) {
                amount *= this.store.logic_wager.value;
            }

            pay.innerHTML = this.formatOddsOrCurrency(amount);
        }
    }

    protected formatOddsOrCurrency(amount: number): string {
        return this._settings.get('showOddsUpfront') ? `${amount}/1` : this._currencyFormatter.formatCents(amount);
    }
}
